import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UserCheck, Radar, BadgeDollarSign, ClipboardList, Zap, Star } from "lucide-react";

const paths = {
  guard: {
    label: "For Security Guards",
    steps: [
      { icon: UserCheck, title: "Get verified once", body: "Upload your guard card and license. We verify against state records and run the background check. Approved in under 24 hours." },
      { icon: Radar, title: "Claim shifts near you", body: "Open the app, see vetted shifts with pay upfront, tap to claim. Your rating unlocks higher-paying work." },
      { icon: BadgeDollarSign, title: "Work, checkout, get paid direct", body: "GPS check-in on arrival, checkout when done. The venue pays you directly at the agreed rate — no agency taking a cut." },
    ],
  },
  venue: {
    label: "For Venues & Businesses",
    steps: [
      { icon: ClipboardList, title: "Post a shift in 60 seconds", body: "Set the role, pay rate, dress code, and site details. It goes live to verified guards instantly." },
      { icon: Zap, title: "Watch it fill in minutes", body: "Licensed guards claim in real time. You see their rating, experience, and ETA the moment they accept." },
      { icon: Star, title: "Rate and repeat", body: "Rate each guard after the shift. Favorites get priority access to your future posts. No-shows are banned — permanently." },
    ],
  },
};

export default function HowItWorks() {
  const [path, setPath] = useState("guard");
  const current = paths[path];

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-col justify-between gap-8 md:flex-row md:items-end"
        >
          <div>
            <p className="font-mono-tag text-[11px] text-amber-500">The workflow</p>
            <h2 className="font-display mt-4 max-w-xl text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-5xl">
              Three steps. Zero friction.
            </h2>
          </div>
          <div className="flex gap-2">
            {Object.entries(paths).map(([key, p]) => (
              <button
                key={key}
                data-testid={`hiw-tab-${key}`}
                onClick={() => setPath(key)}
                className={`rounded-md px-5 py-3 font-display text-sm font-bold uppercase transition-all duration-200 ${
                  path === key ? "bg-slate-50 text-[#07090e]" : "border border-white/10 text-slate-400 hover:text-slate-50"
                }`}
              >
                {key === "guard" ? "Guards" : "Venues"}
              </button>
            ))}
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={path}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3"
          >
            {current.steps.map((s, i) => (
              <motion.div
                key={s.title}
                data-testid={`hiw-step-${path}-${i + 1}`}
                whileHover={{ y: -5 }}
                className="group relative rounded-xl border border-white/8 bg-[#0f141c] p-8 transition-colors duration-300 hover:border-amber-500/30"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500 transition-colors duration-300 group-hover:bg-amber-500 group-hover:text-[#07090e]">
                    <s.icon className="h-6 w-6" strokeWidth={1.75} />
                  </span>
                  <span className="font-mono-tag text-[10px] text-slate-600">Step 0{i + 1}</span>
                </div>
                <h3 className="font-display mt-7 text-2xl font-bold uppercase leading-tight text-slate-50">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
